import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

export interface MultiSelectOption {
  value: string;
  label?: string;
  count?: number;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Optional text shown above the option list. */
  searchPlaceholder?: string;
  /** Disables the trigger button. */
  disabled?: boolean;
  /** Width of the popover. Defaults to `min-w-[16rem]`. */
  popoverWidthClass?: string;
}

/**
 * Lightweight multi-select: a styled trigger button that opens a popover with
 * a search box + checkbox list. Closes on outside click and on Escape.
 *
 * Designed to drop into the Pipeline filter grid as a sibling of <Input>/<Select>.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Any',
  searchPlaceholder = 'Search…',
  disabled = false,
  popoverWidthClass,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click and Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const valueSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => (o.label ?? o.value).toLowerCase().includes(q));
  }, [options, query]);

  const summary = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const o = options.find((x) => x.value === value[0]);
      return o?.label ?? value[0];
    }
    return `${value.length} selected`;
  }, [value, options, placeholder]);

  const toggle = (v: string) => {
    if (valueSet.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors',
          'hover:border-brand-navy-300 focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          value.length === 0 && 'text-slate-400',
        )}
      >
        <span className="truncate">{summary}</span>
        <svg
          aria-hidden
          viewBox="0 0 10 6"
          className={clsx('ml-2 h-2.5 w-2.5 shrink-0 text-slate-500 transition-transform', open && 'rotate-180')}
        >
          <path d="M0 0 L5 6 L10 0" fill="currentColor" />
        </svg>
      </button>

      {value.length > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange([]);
          }}
          title="Clear selection"
          className="absolute right-7 top-1/2 -translate-y-1/2 rounded-full px-1 text-xs text-slate-400 hover:text-slate-700"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className={clsx(
            'absolute left-0 z-40 mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-floating',
            popoverWidthClass ?? 'min-w-full',
          )}
        >
          <div className="border-b border-slate-100 p-2">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
            <div className="mt-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
              <span>{value.length} selected</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-brand-navy-700 hover:underline"
                  onClick={() => onChange(filtered.map((o) => o.value))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-slate-500 hover:underline"
                  onClick={() => onChange([])}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
          <ul className="max-h-56 overflow-y-auto py-1 text-sm">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-slate-400">No matches</li>
            )}
            {filtered.map((o) => {
              const checked = valueSet.has(o.value);
              return (
                <li key={o.value}>
                  <label
                    className={clsx(
                      'flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-brand-navy-50',
                      checked && 'bg-brand-navy-50/60',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
                    />
                    <span className="flex-1 truncate text-slate-800">{o.label ?? o.value}</span>
                    {typeof o.count === 'number' && (
                      <span className="text-[11px] text-slate-400">{o.count.toLocaleString()}</span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
